import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../store';
import { localApi, type QuestionChangeRequest } from '../utils/local-api';

type RequestStatusFilter = 'pending' | 'approved' | 'rejected' | 'cancelled' | 'all';

const statusTone: Record<QuestionChangeRequest['status'], string> = {
  pending: 'bg-amber-100 text-amber-800',
  approved: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
  cancelled: 'bg-gray-100 text-gray-700'
};

const fieldLabels: Record<string, string> = {
  question_text: '题干',
  level: '等级',
  part: 'Part',
  media: '媒体',
  options: '选项',
  correct_index: '正确答案',
  explanation: '解释',
  question_group: '题组 ID',
  group_seq: '题组顺序'
};

function formatValue(value: unknown): string {
  if (value == null || value === '') return '(空)';
  if (Array.isArray(value)) return value.join(' | ');
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function buildDiffRows(request: QuestionChangeRequest) {
  const original = request.original_snapshot || {};
  const proposed = request.proposed_snapshot || {};
  const keys = ['question_text', 'level', 'part', 'media', 'options', 'correct_index', 'explanation', 'question_group', 'group_seq'];
  return keys
    .map((key) => ({
      key,
      label: fieldLabels[key] || key,
      before: formatValue(original[key]),
      after: formatValue(proposed[key])
    }))
    .filter((row) => request.change_type === 'delete'
      ? row.before !== '(空)'
      : request.change_type === 'create'
        ? row.after !== '(空)'
        : row.before !== row.after);
}

const AdminQuestionApprovals: React.FC = () => {
  const { token } = useAuthStore();
  const [status, setStatus] = useState<RequestStatusFilter>('pending');
  const [requests, setRequests] = useState<QuestionChangeRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [actingId, setActingId] = useState<string | null>(null);
  const [rejectingRequestId, setRejectingRequestId] = useState<string | null>(null);
  const [rejectComment, setRejectComment] = useState('');

  const loadRequests = async () => {
    if (!token) return;
    try {
      setLoading(true);
      setError('');
      const response = await localApi.getAdminQuestionChangeRequests(token, status);
      setRequests(response.requests || []);
    } catch (e: any) {
      setError(e?.message || '加载审批记录失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRequests();
  }, [token, status]);

  const pendingCount = useMemo(
    () => requests.filter((request) => request.status === 'pending').length,
    [requests]
  );

  const handleReview = async (requestId: string, action: 'approve' | 'reject') => {
    if (!token) return;
    try {
      setActingId(requestId);
      if (action === 'approve') {
        await localApi.approveQuestionChangeRequest(requestId, token);
      } else {
        await localApi.rejectQuestionChangeRequest(requestId, rejectComment.trim(), token);
        setRejectingRequestId(null);
        setRejectComment('');
      }
      await loadRequests();
    } catch (e: any) {
      alert(e?.message || '审批失败');
    } finally {
      setActingId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">题目审批</h1>
            <p className="text-gray-600">查看老师提交的新增、修改、删除申请，并决定是否写入正式题库。</p>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/admin/questions" className="px-3 py-2 border rounded text-sm hover:bg-gray-50">
              返回题库页
            </Link>
            <button onClick={loadRequests} className="px-3 py-2 border rounded text-sm hover:bg-gray-50">
              {loading ? '刷新中...' : '刷新'}
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="flex flex-wrap gap-2">
            {(['pending', 'approved', 'rejected', 'cancelled', 'all'] as RequestStatusFilter[]).map((value) => (
              <button
                key={value}
                onClick={() => setStatus(value)}
                className={`px-3 py-2 rounded text-sm border ${
                  status === value ? 'bg-indigo-600 text-white border-indigo-600' : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                {value}
              </button>
            ))}
          </div>
          <div className="text-sm text-gray-500">
            当前列表 {requests.length} 条，待审批 {pendingCount} 条
          </div>
        </div>
      </div>

      {error && (
        <div className="rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="space-y-4">
        {requests.length === 0 && !loading && (
          <div className="bg-white shadow rounded-lg px-4 py-5 sm:p-6 text-sm text-gray-500">
            当前没有符合筛选条件的审批记录。
          </div>
        )}

        {requests.map((request) => {
          const diffRows = buildDiffRows(request);
          return (
            <div key={request.id} className="bg-white shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6 space-y-4">
                <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                  <div>
                    <div className="text-lg font-semibold text-gray-900">{request.question_id}</div>
                    <div className="text-sm text-gray-500">
                      {request.bank_version} · {request.change_type} · 提交人 {request.submitted_by} · {new Date(request.submitted_at).toLocaleString()}
                    </div>
                    {request.reviewed_at && (
                      <div className="text-xs text-gray-500 mt-1">
                        审批人 {request.reviewed_by || '-'} · {new Date(request.reviewed_at).toLocaleString()}
                      </div>
                    )}
                  </div>
                  <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${statusTone[request.status]}`}>
                    {request.status}
                  </span>
                </div>

                {request.review_comment && (
                  <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                    拒绝备注：{request.review_comment}
                  </div>
                )}

                <div className="overflow-x-auto rounded border border-gray-200">
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium text-gray-600">字段</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-600">原始值</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-600">提交值</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {diffRows.length === 0 ? (
                        <tr>
                          <td colSpan={3} className="px-3 py-3 text-gray-500">
                            当前没有可展示的差异字段。
                          </td>
                        </tr>
                      ) : (
                        diffRows.map((row) => (
                          <tr key={row.key}>
                            <td className="px-3 py-2 font-medium text-gray-900">{row.label}</td>
                            <td className="px-3 py-2 text-gray-600 whitespace-pre-wrap break-words">{row.before}</td>
                            <td className="px-3 py-2 text-gray-900 whitespace-pre-wrap break-words">{row.after}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {request.status === 'pending' && (
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => {
                        setRejectingRequestId(request.id);
                        setRejectComment('');
                      }}
                      disabled={actingId === request.id}
                      className="px-3 py-2 rounded border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-50"
                    >
                      拒绝
                    </button>
                    <button
                      onClick={() => handleReview(request.id, 'approve')}
                      disabled={actingId === request.id}
                      className="px-3 py-2 rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
                    >
                      {actingId === request.id ? '处理中...' : '批准'}
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {rejectingRequestId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
            <h2 className="text-lg font-semibold text-gray-900">拒绝申请</h2>
            <p className="mt-2 text-sm text-gray-600">可选填写拒绝原因，保存后会显示给老师。</p>
            <textarea
              value={rejectComment}
              onChange={(e) => setRejectComment(e.target.value)}
              rows={4}
              placeholder="例如：需要补充依据，或正式题库已更新"
              className="mt-4 w-full rounded-xl border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
            />
            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={() => {
                  if (actingId) return;
                  setRejectingRequestId(null);
                  setRejectComment('');
                }}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                取消
              </button>
              <button
                onClick={() => handleReview(rejectingRequestId, 'reject')}
                disabled={actingId === rejectingRequestId}
                className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50"
              >
                {actingId === rejectingRequestId ? '提交中...' : '确认拒绝'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminQuestionApprovals;
